// CategoryBuilder.jsx
import React, { useState, useEffect } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import Button from "new_components/atoms/Button";
import TextInput from "new_components/molecules/TextInput";
import SelectBox from "new_components/molecules/SelectBox";
import Toggle from "new_components/atoms/Toggle";
import Modal from "new_components/molecules/Modal";
import Tag from "new_components/molecules/Tag";
import Icon from "new_components/atoms/Icon";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import Text from "new_components/atoms/Text";
import colors from "styles/colors";
import typography from "styles/typography";

// Global style to ensure Raleway font is used throughout the component
const CategoryBuilderStyles = createGlobalStyle`
  .category-builder-container * {
    font-family: 'Raleway', sans-serif;
  }
`;

// Fallback colors in case the theme colors are undefined
const fallbackColors = {
  primary: { 500: "#4A90E2" },
  auxiliary: { 
    red: { 500: "#E53935" },
    green: { 500: "#43A047" }
  },
  neutral: { 
    50: "#F9FAFB", 
    100: "#F3F4F6", 
    200: "#E5E7EB", 
    300: "#D1D5DB", 
    500: "#6B7280" 
  },
  text: { 
    dark: "#111827", 
    gray: "#6B7280" 
  },
  background: { 
    white: "#FFFFFF", 
    lightGray: "#F9FAFB" 
  }
};

// Helper function to safely get color values
const getColor = (path) => {
  try {
    // Split the path into parts (e.g., "primary.500" -> ["primary", "500"])
    const parts = path.split('.');
    let result = colors;
    
    // Navigate through the parts
    for (const part of parts) {
      if (result && result[part] !== undefined) {
        result = result[part];
      } else {
        // If any part is undefined, fall back to our fallback colors
        let fallback = fallbackColors;
        for (const p of parts) {
          fallback = fallback[p] || {};
        }
        return fallback || "#CCCCCC"; // Final fallback if nothing else works
      }
    }
    
    return result;
  } catch (error) {
    console.warn(`Error getting color for path: ${path}`, error);
    return "#CCCCCC"; // Default gray as a last resort
  }
};

// Styled components
const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Raleway', sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  color: ${() => getColor('text.dark')};
  margin: 0;
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
`;

// Table Styles
const TableContainer = styled.div`
  margin-top: 24px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-family: 'Raleway', sans-serif;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${() => getColor('background.white')};
  font-family: 'Raleway', sans-serif;
`;

const TableHead = styled.thead`
  background-color: ${() => getColor('neutral.100')};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${() => getColor('neutral.300')};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${() => getColor('neutral.50')};
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: ${() => getColor('text.dark')};
  font-family: 'Raleway', sans-serif;
`;

const TableCell = styled.td`
  padding: 16px;
  vertical-align: middle;
  font-family: 'Raleway', sans-serif;
`;

const ActionCell = styled(TableCell)`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  background-color: ${() => getColor('background.lightGray')};
  border-radius: 8px;
  text-align: center;
  margin-top: 24px;
  font-family: 'Raleway', sans-serif;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  font-family: 'Raleway', sans-serif;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  font-family: 'Raleway', sans-serif;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  color: ${() => getColor('text.dark')};
  font-family: 'Raleway', sans-serif;
`;

const ToastContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  font-family: 'Raleway', sans-serif;
`;

const Toast = styled.div`
  background-color: ${({ type }) => 
    type === 'success' ? getColor('auxiliary.green.500') : 
    type === 'error' ? getColor('auxiliary.red.500') : 
    getColor('primary.500')};
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Raleway', sans-serif;
`;

const OptionsSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${() => getColor('neutral.300')};
  font-family: 'Raleway', sans-serif;
`;

const OptionsList = styled.div`
  margin-top: 12px;
  margin-bottom: 16px;
  font-family: 'Raleway', sans-serif;
`;

const OptionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-radius: 4px;
  background-color: ${() => getColor('neutral.100')};
  margin-bottom: 8px;
  font-family: 'Raleway', sans-serif;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${() => getColor('text.gray')};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    background-color: ${() => getColor('neutral.200')};
  }
`;

const OptionInput = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  font-family: 'Raleway', sans-serif;
`;

const ModalContent = styled.div`
  padding-right: 4px;
  font-family: 'Raleway', sans-serif;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  font-family: 'Raleway', sans-serif;
`;

// Custom styled Text component with Raleway font
const StyledText = styled(Text)`
  font-family: 'Raleway', sans-serif !important;
`;

// Mock data for initial categories
const initialCategories = [
  {
    id: 1,
    name: "Department",
    description: "Department of the respondent",
    type: "text",
    isPrivate: false,
    inUse: true,
    options: ["Engineering", "Marketing", "Sales", "HR", "Finance"]
  },
  {
    id: 2,
    name: "Age Range",
    description: "Age range of the respondent",
    type: "text",
    isPrivate: false,
    inUse: false,
    options: ["18-24", "25-34", "35-44", "45-54", "55+"]
  },
  {
    id: 3,
    name: "Hire Date",
    description: "When the respondent was hired",
    type: "datetime",
    isPrivate: false,
    inUse: true,
    options: []
  },
  {
    id: 4,
    name: "Contact Number",
    description: "Phone number of the respondent",
    type: "phone",
    isPrivate: true,
    inUse: false,
    options: []
  }
];

const categoryTypeOptions = [
  { label: "Text", value: "text" },
  { label: "Date Time", value: "datetime" },
  { label: "Phone Number", value: "phone" }
];

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

// Onboarding styled components
const OnboardingCard = styled.div`
  background-color: ${() => getColor('background.white')};
  border-radius: 12px;
  padding: 32px;
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  font-family: 'Raleway', sans-serif;
  animation: ${fadeIn} 0.5s ease-out;
`;

const OnboardingTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${() => getColor('text.dark')};
  margin-bottom: 16px;
  font-family: 'Raleway', sans-serif;
  animation: ${slideUp} 0.5s ease-out;
`;

const OnboardingDescription = styled.p`
  font-size: 16px;
  color: ${() => getColor('text.gray')};
  margin-bottom: 32px;
  font-family: 'Raleway', sans-serif;
  animation: ${slideUp} 0.5s ease-out 0.1s;
  animation-fill-mode: both;
`;

const BenefitContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  text-align: left;
  font-family: 'Raleway', sans-serif;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background-color: ${() => getColor('neutral.50')};
  border-radius: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  animation: ${slideUp} 0.5s ease-out ${props => 0.2 + props.index * 0.1}s;
  animation-fill-mode: both;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  font-family: 'Raleway', sans-serif;
`;

const BenefitIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${() => getColor('primary.500')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  flex-shrink: 0;
  font-family: 'Raleway', sans-serif;
  transition: transform 0.3s ease;
  
  &:hover {
    animation: ${pulse} 1s infinite;
  }
`;

const BenefitContent = styled.div`
  flex: 1;
  font-family: 'Raleway', sans-serif;
`;

const BenefitTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${() => getColor('text.dark')};
  margin-bottom: 4px;
  font-family: 'Raleway', sans-serif;
`;

const BenefitText = styled.p`
  font-size: 14px;
  color: ${() => getColor('text.gray')};
  margin: 0;
  font-family: 'Raleway', sans-serif;
`;

const IllustrationContainer = styled.div`
  margin-bottom: 24px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const GetStartedButton = styled(Button)`
  animation: ${fadeIn} 0.5s ease-out 0.6s;
  animation-fill-mode: both;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const CategoryBuilder = () => {
  const [categories, setCategories] = useState(initialCategories);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [newOption, setNewOption] = useState("");
  
  // Check if onboarding should be shown
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('categoryBuilderOnboardingSeen');
    if (!hasSeenOnboarding) {
      setIsOnboardingOpen(true);
    }
  }, []);
  
  // Mark onboarding as seen when closed
  const handleCloseOnboarding = () => {
    localStorage.setItem('categoryBuilderOnboardingSeen', 'true');
    setIsOnboardingOpen(false);
  };
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "text",
    isPrivate: false,
    options: []
  });
  
  // Reset form data when modal closes
  useEffect(() => {
    if (!isAddModalOpen && !isEditModalOpen) {
      setFormData({
        name: "",
        description: "",
        type: "text",
        isPrivate: false,
        options: []
      });
      setNewOption("");
      setCurrentCategory(null);
    }
  }, [isAddModalOpen, isEditModalOpen]);
  
  // Fill form data when editing
  useEffect(() => {
    if (currentCategory) {
      setFormData({
        name: currentCategory.name,
        description: currentCategory.description,
        type: currentCategory.type,
        isPrivate: currentCategory.isPrivate,
        options: [...(currentCategory.options || [])]
      });
    }
  }, [currentCategory]);
  
  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Show toast message
  const showToast = (message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    
    // Auto-remove toast after 3 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 3000);
  };
  
  // Add new category
  const handleAddCategory = () => {
    const newCategory = {
      id: Date.now(),
      name: formData.name,
      description: formData.description,
      type: formData.type,
      isPrivate: formData.isPrivate,
      inUse: false,
      options: formData.type === "text" ? [...formData.options] : []
    };
    
    setCategories(prev => [...prev, newCategory]);
    setIsAddModalOpen(false);
    showToast(`Category "${formData.name}" has been created successfully!`, 'success');
  };
  
  // Edit category
  const handleEditCategory = () => {
    if (!currentCategory) return;
    
    // Check if only name is changed for in-use categories
    if (currentCategory.inUse) {
      if (formData.type !== currentCategory.type || 
          formData.isPrivate !== currentCategory.isPrivate) {
        showToast("You can only change the name and description of categories that are in use.", 'error');
        return;
      }
    }
    
    const updatedCategories = categories.map(cat => {
      if (cat.id === currentCategory.id) {
        return {
          ...cat,
          name: formData.name,
          description: formData.description,
          type: formData.type,
          isPrivate: formData.isPrivate,
          options: formData.type === "text" ? [...formData.options] : []
        };
      }
      return cat;
    });
    
    setCategories(updatedCategories);
    setIsEditModalOpen(false);
    showToast(`Category "${formData.name}" has been updated successfully!`, 'success');
  };
  
  // Delete category
  const handleDeleteCategory = (category) => {
    if (category.inUse) {
      showToast(`Cannot delete "${category.name}" because it is currently in use.`, 'error');
      return;
    }
    
    const updatedCategories = categories.filter(cat => cat.id !== category.id);
    setCategories(updatedCategories);
    showToast(`Category "${category.name}" has been deleted successfully!`, 'success');
  };
  
  // Add option to category
  const handleAddOption = () => {
    if (!newOption.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, newOption.trim()]
    }));
    
    setNewOption("");
  };
  
  // Remove option from category
  const handleRemoveOption = (optionIndex) => {
    try {
      setFormData(prev => ({
        ...prev,
        options: prev.options.filter((_, index) => index !== optionIndex)
      }));
    } catch (error) {
      console.error("Error removing option:", error);
      showToast("There was an error removing the option. Please try again.", 'error');
    }
  };
  
  // Render toast messages
  const renderToasts = () => {
    return (
      <ToastContainer>
        {toasts.map(toast => (
          <Toast key={toast.id} type={toast.type}>
            {(() => {
              try {
                const iconName = 
                  toast.type === 'success' ? ICON_SET.checkCircle : 
                  toast.type === 'error' ? ICON_SET.alertCircle : 
                  ICON_SET.info;
                
                // Only render the Icon if we have a valid icon name
                return iconName ? (
                  <Icon 
                    name={iconName} 
                    size={18} 
                    color="white" 
                  />
                ) : (
                  <span style={{ marginRight: '8px', fontWeight: 'bold' }}>
                    {toast.type === 'success' ? '✓' : toast.type === 'error' ? '!' : 'i'}
                  </span>
                );
              } catch (error) {
                console.error("Error rendering toast icon:", error);
                // Fallback to a simple text character if Icon fails
                return (
                  <span style={{ marginRight: '8px', fontWeight: 'bold' }}>
                    {toast.type === 'success' ? '✓' : toast.type === 'error' ? '!' : 'i'}
                  </span>
                );
              }
            })()}
            {toast.message}
          </Toast>
        ))}
      </ToastContainer>
    );
  };
  
  // Render options section in modal
  const renderOptionsSection = () => {
    // Only show options for text type categories
    if (formData.type !== "text") {
      return null;
    }
    
    return (
      <OptionsSection>
        <StyledText style={{ fontWeight: 600 }}>Category Options</StyledText>
        <StyledText style={{ fontSize: '14px', color: getColor('text.gray'), marginTop: '4px' }}>
          Add options that users can select for this category
        </StyledText>
        
        <OptionsList>
          {formData.options && formData.options.length > 0 ? (
            formData.options.map((option, index) => (
              <OptionItem key={index}>
                <StyledText>{option}</StyledText>
                <DeleteButton 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      handleRemoveOption(index);
                    } catch (error) {
                      console.error("Error removing option:", error);
                      showToast("There was an error removing the option. Please try again.", 'error');
                    }
                  }}
                  aria-label="Delete option"
                  type="button"
                >
                  ✕
                </DeleteButton>
              </OptionItem>
            ))
          ) : (
            <StyledText style={{ color: getColor('text.gray'), fontSize: '14px' }}>
              No options added yet
            </StyledText>
          )}
        </OptionsList>
        
        <OptionInput>
          <TextInput
            placeholder="Enter option"
            value={newOption}
            onChange={setNewOption}
          />
          <Button 
            variant="secondary" 
            onClick={handleAddOption}
            disabled={!newOption.trim()}
          >
            Add
          </Button>
        </OptionInput>
      </OptionsSection>
    );
  };
  
  // Add Category Modal
  const renderAddModal = () => {
    const modalBody = (
      <ModalContent>
        <FormGroup>
          <TextInput
            label="Category Name"
            value={formData.name}
            onChange={(value) => handleInputChange("name", value)}
            placeholder="Enter category name"
          />
        </FormGroup>
        
        <FormGroup>
          <TextInput
            label="Description"
            value={formData.description}
            onChange={(value) => handleInputChange("description", value)}
            placeholder="Enter category description"
          />
        </FormGroup>
        
        <FormGroup>
          <SelectBox
            label="Category Type"
            options={categoryTypeOptions}
            value={formData.type}
            onSelect={(value) => handleInputChange("type", value)}
            width="100%"
          />
        </FormGroup>
        
        <FormGroup>
          <ToggleContainer>
            <Toggle
              isActive={formData.isPrivate}
              onToggle={(value) => handleInputChange("isPrivate", value)}
            />
            <ToggleLabel>
              Privacy Protection (Never allow user data to be viewed)
            </ToggleLabel>
          </ToggleContainer>
        </FormGroup>
        
        {renderOptionsSection()}
      </ModalContent>
    );
    
    const modalFooter = (
      <ModalFooter>
        <Button 
          variant="secondary" 
          onClick={() => setIsAddModalOpen(false)}
          style={{ marginRight: '12px' }}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleAddCategory}
          disabled={!formData.name}
        >
          Add Category
        </Button>
      </ModalFooter>
    );
    
    return (
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        modalHeading="Add New Category"
        modalBody={modalBody}
        modalFooter={modalFooter}
        modalWidth="600px"
        modalHeight="auto"
      />
    );
  };
  
  // Edit Category Modal
  const renderEditModal = () => {
    const isInUse = currentCategory?.inUse;
    
    const modalBody = (
      <ModalContent>
        <FormGroup>
          <TextInput
            label="Category Name"
            value={formData.name}
            onChange={(value) => handleInputChange("name", value)}
            placeholder="Enter category name"
          />
        </FormGroup>
        
        <FormGroup>
          <TextInput
            label="Description"
            value={formData.description}
            onChange={(value) => handleInputChange("description", value)}
            placeholder="Enter category description"
          />
        </FormGroup>
        
        <FormGroup>
          <SelectBox
            label="Category Type"
            options={categoryTypeOptions}
            value={formData.type}
            onSelect={(value) => !isInUse && handleInputChange("type", value)}
            width="100%"
            disabled={isInUse}
          />
          {isInUse && (
            <StyledText style={{ color: getColor('auxiliary.red.500'), marginTop: '4px', fontSize: '12px' }}>
              Type cannot be changed for categories in use
            </StyledText>
          )}
        </FormGroup>
        
        <FormGroup>
          <ToggleContainer>
            <Toggle
              isActive={formData.isPrivate}
              onToggle={(value) => !isInUse && handleInputChange("isPrivate", value)}
            />
            <ToggleLabel>
              Privacy Protection (Never allow user data to be viewed)
            </ToggleLabel>
          </ToggleContainer>
          {isInUse && (
            <StyledText style={{ color: getColor('auxiliary.red.500'), marginTop: '4px', fontSize: '12px' }}>
              Privacy settings cannot be changed for categories in use
            </StyledText>
          )}
        </FormGroup>
        
        {formData.type === "text" && renderOptionsSection()}
      </ModalContent>
    );
    
    const modalFooter = (
      <ModalFooter>
        <Button 
          variant="secondary" 
          onClick={() => setIsEditModalOpen(false)}
          style={{ marginRight: '12px' }}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleEditCategory}
          disabled={!formData.name}
        >
          Save Changes
        </Button>
      </ModalFooter>
    );
    
    return (
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        modalHeading="Edit Category"
        modalBody={modalBody}
        modalFooter={modalFooter}
        modalWidth="600px"
        modalHeight="auto"
      />
    );
  };

  // Open edit modal with category data
  const handleOpenEditModal = (category) => {
    setCurrentCategory(category);
    setIsEditModalOpen(true);
  };

  // Render onboarding modal
  const renderOnboardingModal = () => {
    const modalBody = (
      <OnboardingCard>
        <IllustrationContainer>
          {(() => {
            try {
              // Try to use the category icon from ICON_SET if available
              return ICON_SET.category ? (
                <Icon 
                  name={ICON_SET.category} 
                  size={80} 
                  color={getColor('primary.500')} 
                />
              ) : (
                // Fallback to a simple illustration
                <div style={{ fontSize: '80px', margin: '0 auto', width: 'fit-content' }}>
                  🗂️
                </div>
              );
            } catch (error) {
              console.error("Error rendering category icon:", error);
              // Fallback to emoji if Icon component fails
              return (
                <div style={{ fontSize: '80px', margin: '0 auto', width: 'fit-content' }}>
                  🗂️
                </div>
              );
            }
          })()}
        </IllustrationContainer>
        
        <OnboardingTitle>Welcome to Category Builder!</OnboardingTitle>
        <OnboardingDescription>
          Categories are a powerful way to organize and manage your data. Here's why they're important:
        </OnboardingDescription>
        
        <BenefitContainer>
          <BenefitItem index={0}>
            <BenefitIcon>📊</BenefitIcon>
            <BenefitContent>
              <BenefitTitle>Predictable Data Analysis</BenefitTitle>
              <BenefitText>
                Categories help you analyze data in a more predictable way, making it easier to spot trends and patterns.
              </BenefitText>
            </BenefitContent>
          </BenefitItem>
          
          <BenefitItem index={1}>
            <BenefitIcon>🔒</BenefitIcon>
            <BenefitContent>
              <BenefitTitle>Efficient Data Access</BenefitTitle>
              <BenefitText>
                They help to allocate data access, ensuring the right people have access to the right information.
              </BenefitText>
            </BenefitContent>
          </BenefitItem>
          
          <BenefitItem index={2}>
            <BenefitIcon>🏷️</BenefitIcon>
            <BenefitContent>
              <BenefitTitle>Consistent Data Grouping</BenefitTitle>
              <BenefitText>
                Categories allow you to group data consistently and manage the tags associated with your data.
              </BenefitText>
            </BenefitContent>
          </BenefitItem>
        </BenefitContainer>
      </OnboardingCard>
    );
    
    const modalFooter = (
      <ModalFooter>
        <GetStartedButton 
          variant="primary" 
          onClick={handleCloseOnboarding}
        >
          Get Started
        </GetStartedButton>
      </ModalFooter>
    );
    
    return (
      <Modal
        isOpen={isOnboardingOpen}
        onClose={handleCloseOnboarding}
        modalHeading=""
        modalBody={modalBody}
        modalFooter={modalFooter}
        modalWidth="650px"
        modalHeight="auto"
      />
    );
  };

  return (
    <Container className="category-builder-container">
      <CategoryBuilderStyles />
      <Header>
        <Title>Category Builder</Title>
        <Button 
          variant="primary" 
          onClick={() => setIsAddModalOpen(true)}
        >
          Add New Category
        </Button>
      </Header>
      
      {categories.length > 0 ? (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Description</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Options</TableHeaderCell>
                <TableHeaderCell>Privacy</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHead>
            <tbody>
              {categories.map(category => (
                <TableRow key={category.id}>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>{category.description}</TableCell>
                  <TableCell>
                    {categoryTypeOptions.find(opt => opt.value === category.type)?.label}
                  </TableCell>
                  <TableCell>
                    {category.type === "text" ? (
                      category.options && category.options.length > 0 ? (
                        <Tag>{category.options.length} options</Tag>
                      ) : (
                        <StyledText style={{ color: getColor('text.gray') }}>No options</StyledText>
                      )
                    ) : (
                      <StyledText style={{ color: getColor('text.gray') }}>N/A</StyledText>
                    )}
                  </TableCell>
                  <TableCell>
                    {category.isPrivate ? (
                      <Tag selected>Private</Tag>
                    ) : (
                      <StyledText>Public</StyledText>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tag selected={category.inUse}>
                      {category.inUse ? "In Use" : "Not In Use"}
                    </Tag>
                  </TableCell>
                  <ActionCell>
                    <Button 
                      variant="secondary" 
                      onClick={() => handleOpenEditModal(category)}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="secondary" 
                      onClick={() => handleDeleteCategory(category)}
                      disabled={category.inUse}
                    >
                      Delete
                    </Button>
                  </ActionCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState>
          {(() => {
            try {
              // Only render the Icon if ICON_SET.folder exists
              return ICON_SET.folder ? (
                <Icon 
                  name={ICON_SET.folder} 
                  size={48} 
                  color={getColor('neutral.500')} 
                  style={{ marginBottom: '16px' }}
                />
              ) : (
                <div style={{ 
                  fontSize: '48px', 
                  color: getColor('neutral.500'), 
                  marginBottom: '16px' 
                }}>
                  📁
                </div>
              );
            } catch (error) {
              console.error("Error rendering folder icon:", error);
              // Fallback to a simple folder emoji if Icon fails
              return (
                <div style={{ 
                  fontSize: '48px', 
                  color: getColor('neutral.500'), 
                  marginBottom: '16px' 
                }}>
                  📁
                </div>
              );
            }
          })()}
          <StyledText>No categories found. Create your first category to get started.</StyledText>
        </EmptyState>
      )}
      
      {renderAddModal()}
      {renderEditModal()}
      {renderOnboardingModal()}
      {renderToasts()}
    </Container>
  );
};

export default CategoryBuilder;